import { Heart, Star, ExternalLink } from "lucide-react";
import type { Product } from "@/data/products";
import { trackOfferClick } from "@/lib/analytics";

type Props = {
  product: Product;
  isFav: boolean;
  onToggleFav: (id: number) => void;
  onOpenDetail: (p: Product) => void;
};

const fmt = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function ProductCard({ product, isFav, onToggleFav, onOpenDetail }: Props) {
  const hasDiscount = product.desconto > 0 && product.precoOriginal > product.precoAtual;

  const open = () => {
    trackOfferClick(product, "card");
    window.open(product.linkAfiliado, "_blank", "noopener,noreferrer");
  };

  return (
    <article
      data-testid="product-card"
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-[var(--shadow-card)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-soft)]"
    >
      <button
        onClick={() => onOpenDetail(product)}
        className="relative aspect-square overflow-hidden bg-muted"
        aria-label={`Ver detalhes de ${product.nome}`}
      >
        <img
          src={product.imagem}
          alt={product.nome}
          loading="lazy"
          width={640}
          height={640}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {hasDiscount ? (
          <span
            data-testid="product-card-sale-badge"
            aria-hidden
            className="absolute left-3 top-3 inline-flex items-center rounded-full bg-[var(--sale)] px-2 py-0.5 text-[11px] font-semibold text-white shadow-sm"
          >
            -{product.desconto}%
          </span>
        ) : null}
        <span aria-hidden className="absolute bottom-3 left-3 rounded-full bg-card/90 px-2 py-0.5 text-[10px] font-medium text-foreground/80 backdrop-blur">
          {product.loja}
        </span>
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); onToggleFav(product.id); }}
        aria-label="Favoritar"
        className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-card/90 text-foreground/70 shadow-sm backdrop-blur transition hover:scale-110 hover:text-primary"
      >
        <Heart className={`h-4 w-4 transition ${isFav ? "fill-primary text-primary scale-110" : ""}`} />
      </button>

      <div className="flex flex-1 flex-col gap-2 p-3 sm:p-4">
        <h3
          data-testid="product-card-title"
          className="min-h-[3.5rem] line-clamp-2 text-sm font-medium leading-snug text-foreground/90 sm:min-h-[3.75rem]"
        >
          {product.nome}
        </h3>
        <div
          data-testid="product-card-rating"
          className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
        >
          <Star className="h-3.5 w-3.5 fill-[var(--star)] text-[var(--star)]" />
          <span className="font-semibold text-foreground/80">{product.avaliacao}</span>
          <span>({product.quantidadeAvaliacoes})</span>
        </div>
        <div className="text-xs font-medium text-foreground/70">
          {product.vendidos > 0 ? `${product.vendidos.toLocaleString("pt-BR")} vendas` : "Novo no catálogo"}
        </div>
        <div className="mt-auto grid gap-2 pt-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <div data-testid="product-card-price-block" className="min-w-0">
            {hasDiscount ? (
              <div
                data-testid="product-card-original-price"
                className="text-[11px] text-muted-foreground line-through"
              >
                {fmt(product.precoOriginal)}
              </div>
            ) : null}
            <div
              data-testid="product-card-price"
              className={`font-display leading-none sm:text-xl ${hasDiscount ? "text-[1.9rem] text-primary" : "text-[1.7rem] text-foreground"}`}
            >
              {fmt(product.precoAtual)}
            </div>
          </div>
          <button
            onClick={open}
            data-testid="product-card-cta"
            className="inline-flex w-full items-center justify-center gap-1 rounded-full bg-foreground px-3 py-2 text-sm font-medium text-background transition hover:bg-primary sm:w-auto sm:px-3.5 sm:text-xs"
          >
            Ver oferta <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </article>
  );
}
